import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { Menu, Search, Bell, User, Mail, Calendar, Clock, Pencil, LogOut, X } from 'lucide-react';

// Map a notification's `type` to the admin page it relates to.
// Extend this as new notification types are added on the backend.
const TYPE_ROUTE_MAP = [
  { prefix: 'feedback',     path: '/admin/feedback' },
  { prefix: 'volunteer',    path: '/admin/volunteers' },
  { prefix: 'contribution', path: '/admin/contributions' },
  { prefix: 'event',        path: '/admin/events' },
  { prefix: 'news',         path: '/admin/news' },
  { prefix: 'user',         path: '/admin/users' },
  { prefix: 'download',     path: '/admin/downloads' },
  { prefix: 'sector',       path: '/admin/sectors' },
  { prefix: 'expert',       path: '/admin/experts' },
];

function getNotificationLink(type = '') {
  const match = TYPE_ROUTE_MAP.find(({ prefix }) => type.startsWith(prefix));
  return match ? match.path : '/admin/dashboard';
}

function timeAgo(dateStr, language) {
  if (!dateStr) return '';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return language === 'ne' ? 'भर्खरै' : 'just now';
  if (mins < 60) return language === 'ne' ? `${mins} मिनेट अगाडि` : `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return language === 'ne' ? `${hrs} घण्टा अगाडि` : `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return language === 'ne' ? `${days} दिन अगाडि` : `${days}d ago`;
}

function formatJoined(dateStr, language) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(language === 'ne' ? 'ne-NP' : 'en-US', {
    year: 'numeric',
    month: 'long',
  });
}

function formatLastLogin(dateStr, language) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString(language === 'ne' ? 'ne-NP' : 'en-US', {
    day: 'numeric',
    month: 'numeric',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const ROLE_LABELS_TOPBAR = {
  admin: 'admin', editor: 'editor', reporter: 'reporter',
  volunteer: 'volunteer', contributor: 'contributor', public: 'public',
};

const AdminTopbar = ({ title, onToggleSidebar, language, onToggleLanguage }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifs, setLoadingNotifs] = useState(false);

  const notifRef = useRef(null);
  const profileRef = useRef(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    fetchNotifications();
    // Refresh periodically so the badge stays current even if the dropdown isn't open
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  
  useEffect(() => {
    function handleClickOutside(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfile(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function fetchNotifications() {
    setLoadingNotifs(true);
    try {
      const res = await api.get('/notification');
      const data = res.data?.data ?? res.data?.notifications ?? res.data ?? [];
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoadingNotifs(false);
    }
  }

  async function handleNotificationClick(n) {
    setShowNotifications(false);
    if (!n.read) {
      try {
        await api.put(`/notification/${n._id}/read`);
        setNotifications(prev => prev.map(x => x._id === n._id ? { ...x, read: true } : x));
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }
    navigate(getNotificationLink(n.type));
  }

  async function handleMarkAllRead() {
    try {
      await api.put('/notification/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  }

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'AD';

  const handleLogout = async () => {
    setShowProfile(false);
    await logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-char-100">
      <div className="flex items-center justify-between px-4 lg:px-6 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleSidebar}
            className="rounded-xl p-2 text-char-500 hover:bg-char-50 transition-colors lg:hidden"
          >
            <Menu size={20} />
          </button>
          <h1 className="text-lg font-extrabold text-char-900">{title}</h1>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden sm:flex items-center gap-2 rounded-xl bg-char-50 px-3 py-2 text-char-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-primary-200 focus-within:text-char-600 transition-all">
            <Search size={16} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={language === 'ne' ? 'खोज्नुहोस्...' : 'Search...'}
              className="w-40 bg-transparent text-sm text-char-900 outline-none placeholder:text-char-400 lg:w-52"
            />
          </div>

          <button
            onClick={onToggleLanguage}
            className="flex items-center gap-1.5 rounded-xl border border-char-100 px-3 py-2 text-xs font-bold uppercase tracking-wider text-char-600 hover:bg-char-50 transition-colors"
          >
            {language === 'ne' ? 'ने' : 'EN'}
          </button>

          {/* Notifications */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => { setShowNotifications(s => !s); setShowProfile(false); }}
              className="relative rounded-xl p-2 text-char-500 hover:bg-char-50 transition-colors"
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute right-0.5 top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-error px-1 text-[9px] font-bold text-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 top-full mt-2 w-80 rounded-2xl border border-char-100 bg-white shadow-lg animate-in fade-in slide-in-from-top-2">
                <div className="flex items-center justify-between border-b border-char-100 px-4 py-3">
                  <p className="text-sm font-extrabold text-char-900">
                    {language === 'ne' ? 'सूचनाहरू' : 'Notifications'}
                  </p>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllRead}
                      className="text-xs font-semibold text-primary hover:underline"
                    >
                      {language === 'ne' ? 'सबै पढियो चिन्ह लगाउनुहोस्' : 'Mark all read'}
                    </button>
                  )}
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {loadingNotifs && (
                    <div className="px-4 py-6 text-center text-xs text-char-400">
                      {language === 'ne' ? 'लोड हुँदैछ…' : 'Loading…'}
                    </div>
                  )}
                  {!loadingNotifs && notifications.length === 0 && (
                    <div className="px-4 py-6 text-center text-xs text-char-400">
                      {language === 'ne' ? 'कुनै सूचना छैन' : 'No notifications'}
                    </div>
                  )}
                  {!loadingNotifs && notifications.map((n) => (
                    <button
                      key={n._id}
                      onClick={() => handleNotificationClick(n)}
                      className={`flex w-full items-start gap-3 border-b border-char-50 px-4 py-3 text-left last:border-0 hover:bg-char-50 transition-colors ${
                        !n.read ? 'bg-primary-50/40' : ''
                      }`}
                    >
                      <span
                        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                          n.read ? 'bg-char-200' : 'bg-primary'
                        }`}
                      />
                      <span className="flex-1">
                        <p className="text-sm text-char-700">{n.message}</p>
                        <p className="mt-0.5 text-[11px] text-char-400">{timeAgo(n.createdAt, language)}</p>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>


          <button
            onClick={() => { setShowProfile(true); setShowNotifications(false); }}
            className="flex items-center gap-2 rounded-xl bg-primary-50 px-3 py-1.5 hover:bg-primary-100 transition-colors"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-[11px] font-extrabold text-white">
              {initials}
            </div>
            <span className="hidden text-sm font-bold text-char-800 lg:block">
              {user?.name || 'Admin'}
            </span>
          </button>
        </div>
      </div>

      {/* Profile modal — rendered via portal so it always centers in the true viewport,
          escaping the header's backdrop-filter (which otherwise creates its own
          containing block for fixed-position children) */}
      {showProfile && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-char-900/40 backdrop-blur-sm p-4">
          <div ref={profileRef} className="w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-2xl animate-in fade-in zoom-in-95">
            {/* Header */}
            <div className="relative bg-gradient-to-br from-primary-600 to-primary px-6 pb-14 pt-5">
              <button
                onClick={() => setShowProfile(false)}
                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
              >
                <X size={16} />
              </button>
              <p className="text-xs font-semibold uppercase tracking-wider text-white/70">
                {language === 'ne' ? 'एडमिन प्रोफाइल' : 'Admin Profile'}
              </p>
              <p className="mt-1 text-xl font-extrabold text-white">{user?.name || 'Admin'}</p>
            </div>

            {/* Avatar overlapping header — absolutely positioned with explicit z-index
                so it always paints above the header, regardless of stacking quirks */}
            <div className="relative flex justify-center">
              <div className="absolute -top-10 z-10 flex h-20 w-20 items-center justify-center rounded-full border-4 border-white bg-orange-500 shadow-md">
                <span className="text-2xl font-extrabold text-white">
                  {(user?.name?.[0] || 'A').toUpperCase()}
                </span>
              </div>
            </div>

            <div className="px-6 pb-6 pt-12">
              <div className="flex justify-center">
                <span className="rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-bold text-primary">
                  {ROLE_LABELS_TOPBAR[user?.role] || user?.role || 'admin'}
                </span>
              </div>

              <div className="mt-5 space-y-4">
                <div className="flex items-center gap-2 border-b border-char-100 pb-3">
                  <User size={14} className="text-char-400" />
                  <div>
                    <p className="text-[11px] text-char-400">{language === 'ne' ? 'पूरा नाम' : 'Full Name'}</p>
                    <p className="text-sm font-bold text-char-900">{user?.name || '—'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 border-b border-char-100 pb-3">
                  <Mail size={14} className="text-char-400" />
                  <div>
                    <p className="text-[11px] text-char-400">{language === 'ne' ? 'इमेल' : 'Email'}</p>
                    <p className="text-sm font-bold text-char-900">{user?.email || '—'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-char-50 p-3">
                    <div className="flex items-center gap-1.5 text-char-400">
                      <Calendar size={12} />
                      <p className="text-[10px]">{language === 'ne' ? 'सामेल भएको' : 'Joined'}</p>
                    </div>
                    <p className="mt-1 text-sm font-bold text-char-900">{formatJoined(user?.createdAt, language)}</p>
                  </div>
                  <div className="rounded-xl bg-char-50 p-3">
                    <div className="flex items-center gap-1.5 text-char-400">
                      <Clock size={12} />
                      <p className="text-[10px]">{language === 'ne' ? 'अन्तिम लगइन' : 'Last Login'}</p>
                    </div>
                    <p className="mt-1 text-sm font-bold text-char-900">{formatLastLogin(user?.lastLogin, language)}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => { setShowProfile(false); navigate('/admin/settings'); }}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-primary-600"
                >
                  <Pencil size={15} />
                  {language === 'ne' ? 'प्रोफाइल सम्पादन गर्नुहोस्' : 'Edit Profile'}
                </button>
                <button
                  onClick={handleLogout}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-error-200 px-4 py-2.5 text-sm font-bold text-error transition-colors hover:bg-error-50"
                >
                  <LogOut size={15} />
                  {language === 'ne' ? 'लगआउट' : 'Logout'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
};

export default AdminTopbar;
